import Grid from '@mui/material/Grid';
import { RHFAutocomplete } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { ListPeoplePagination } from '@tyro/people';
import { useCallback } from 'react';
import {
  type Control,
  type UseFormSetFocus,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import {
  type StudentsSearchParty,
  useStudentsSearchProps,
} from '../../../hooks/use-students-search-props';
import type { CustomGroupFormState } from './types';

type StaticStudentsProps = {
  control: Control<CustomGroupFormState>;
  setFocus: UseFormSetFocus<CustomGroupFormState>;
  setValue: UseFormSetValue<CustomGroupFormState>;
};

export const StaticStudents = ({
  setValue,
  setFocus,
  control,
}: StaticStudentsProps) => {
  const { t } = useTranslation(['groups', 'common']);

  const students = useWatch({ control, name: 'staticStudents' });

  const studentsGroups = useStudentsSearchProps({
    unshiftMode: true,
    renderAvatarTags: () => null,
  });

  const removeStudent = useCallback(
    (currentPartyId: number) => {
      setValue(
        'staticStudents',
        students.filter(({ partyId }) => currentPartyId !== partyId),
      );
    },
    [setValue, students],
  );

  return (
    <Grid container gap={2}>
      <Grid size={{ xs: 12 }}>
        <RHFAutocomplete<CustomGroupFormState, StudentsSearchParty, true>
          {...studentsGroups}
          disableClearable
          inputProps={{
            onKeyDown: (event) => {
              if (event.key === 'Backspace' || event.key === 'Delete') {
                event.stopPropagation();
              }
            },
          }}
          controlProps={{
            name: 'staticStudents',
            control,
          }}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <ListPeoplePagination
          people={students}
          emptyTitle={t('groups:noStaticStudents')}
          emptyDescription={t('groups:noStaticStudentsCta')}
          noFoundMessage={t('groups:noStudentsFound')}
          removeLabel={t('groups:removeStudent')}
          onFocus={() => setFocus('staticStudents')}
          onRemove={removeStudent}
        />
      </Grid>
    </Grid>
  );
};
